/**
 * RevenueCat purchase wrapper.
 *
 * On native (iOS/Android): uses RevenueCat Capacitor plugin for IAP.
 * On web: no-ops gracefully. Purchase page shows "available in the app".
 *
 * RevenueCat Capacitor plugin must be installed separately:
 *   npm install @revenuecat/purchases-capacitor
 *   npx cap sync
 */
import { isNativeApp, getPlatform } from './platform';
import { reportPurchaseError } from './metrics';

export interface ProductOffering {
  id: string;
  title: string;
  description: string;
  priceString: string;
  pages: number;
  type: 'pack' | 'subscription';
}

export interface PurchaseResult {
  success: boolean;
  receiptId?: string;
  productId?: string;
  error?: string;
  /** Human-readable diagnostics (RC/StoreKit/Play code + message, offerings
   *  summary). Shown in the failure toast and reported to /metrics/event. */
  detail?: string;
}

let lastInitError = '';

function describeError(e: any): string {
  const code = e?.code ?? e?.errorCode ?? e?.readableErrorCode ?? '';
  const msg = e?.message || e?.underlyingErrorMessage || String(e);
  const under = e?.underlyingErrorMessage && e.underlyingErrorMessage !== msg ? ` / ${e.underlyingErrorMessage}` : '';
  return `${msg}${under}${code !== '' ? ` [code ${code}]` : ''}`.slice(0, 400);
}

/**
 * A cancelled store sheet is not an error (2026-10-02). The iOS plugin rejects with
 * `call.reject(message, code)` — `{ message: 'Purchase was cancelled.', code: '1' }`, no `userCancelled` field
 * (PluginHelperExtensions.swift) — and Android puts RevenueCat's info map into `data`. Until now only
 * `e.userCancelled` was checked, so every iOS cancel showed the "Fehler" toast and was logged as stage=purchase
 * (11 of 11 since 08-31, the reviewer's included; stage=cancelled had 0 rows ever).
 */
export function isPurchaseCancelled(e: any): boolean {
  if (!e) return false;
  if (e.userCancelled === true || e.data?.userCancelled === true || e.userInfo?.userCancelled === true) return true;
  const codes = [e.code, e.errorCode, e.readableErrorCode, e.data?.code, e.data?.readableErrorCode, e.userInfo?.readableErrorCode]
    .filter((c) => c !== undefined && c !== null).map((c) => String(c));
  if (codes.some((c) => c === '1' || c === 'PURCHASE_CANCELLED' || c === 'PURCHASE_CANCELLED_ERROR' || c === 'PurchaseCancelledError')) return true;
  return /cancel/i.test(String(e.message ?? e.errorMessage ?? ''));
}

// Web fallback offerings (shown as info only, no purchasing)
const WEB_OFFERINGS: ProductOffering[] = [
  { id: 'monthly_40', title: 'Monthly', description: '40 pages/month', priceString: '$4.99', pages: 40, type: 'subscription' },
  { id: 'annual_40', title: 'Annual', description: '40 pages/month', priceString: '$39.99/year', pages: 40, type: 'subscription' },
  { id: 'credits_20', title: '20 Pages', description: 'Small pack', priceString: '$3.99', pages: 20, type: 'pack' },
  { id: 'credits_60', title: '60 Pages', description: 'Medium pack', priceString: '$9.99', pages: 60, type: 'pack' },
  { id: 'credits_150', title: '150 Pages', description: 'Large pack', priceString: '$19.99', pages: 150, type: 'pack' },
];

let rcInitialized = false;
let Purchases: any = null;
let initPromise: Promise<boolean> | null = null;

/**
 * Initialize RevenueCat with user ID. Call after login. No-op on web.
 * Returns (and remembers) its promise — true once `Purchases.configure` went through — so the purchase page can
 * wait for it instead of racing it (2026-10-02: a quick tap on the pages badge right after launch rendered the USD
 * placeholder prices in a German app). AuthContext may keep calling it without awaiting.
 */
export function initPurchases(userId: string): Promise<boolean> {
  if (!isNativeApp()) return Promise.resolve(false);
  initPromise = configurePurchases(userId);
  return initPromise;
}

/**
 * Resolves true when RevenueCat is ready for purchases. Waits for an init in flight; starts one for `userId` when
 * none ran yet — or again with `retry` after a failure (the purchase page's "Erneut versuchen").
 */
export function purchasesReady(userId?: string, retry = false): Promise<boolean> {
  if (!isNativeApp()) return Promise.resolve(false);
  if (rcInitialized && Purchases) return Promise.resolve(true);
  if (initPromise && !retry) return initPromise;
  return userId ? initPurchases(userId) : Promise.resolve(false);
}

/** Why the last configure failed ('' when it went through or never ran) — for the purchase page's diagnostics. */
export function purchasesInitError(): string {
  return lastInitError;
}

async function configurePurchases(userId: string): Promise<boolean> {
  try {
    // @ts-ignore — optional dependency, installed only for native builds
    const rc = await import('@revenuecat/purchases-capacitor');
    Purchases = rc.Purchases;
    const apiKey = getPlatform() === 'ios'
      ? process.env.REACT_APP_REVENUECAT_IOS_KEY || ''
      : process.env.REACT_APP_REVENUECAT_ANDROID_KEY || '';

    if (!apiKey) {
      console.warn('RevenueCat API key not configured');
      lastInitError = `no RevenueCat key for ${getPlatform()}`;
      return false;
    }

    await Purchases.configure({ apiKey, appUserID: userId });
    rcInitialized = true;
    lastInitError = '';
    return true;
  } catch (e) {
    console.warn('RevenueCat not available:', e);
    lastInitError = `init: ${describeError(e)}`;
    return rcInitialized;
  }
}

/** The pack the page marks "Beliebteste" — by product id, never by position (RevenueCat's offering order is
 *  credits_20, credits_150, credits_60, so the badge used to sit on the 150 pack). */
export const POPULAR_PACK_ID = 'credits_60';

/** The page counts in stories of 10 pages (the default length): "≈ 2 / 6 / 15 Geschichten à 10 Seiten". */
export const PAGES_PER_STORY = 10;
export function storiesFor(pages: number): number {
  return Math.floor(pages / PAGES_PER_STORY);
}

/** Subscriptions first (the store's order), then the packs by page count ascending (20, 60, 150). */
export function sortOfferings(items: ProductOffering[]): ProductOffering[] {
  const subs = items.filter((o) => o.type === 'subscription');
  const packs = items.filter((o) => o.type === 'pack').sort((a, b) => a.pages - b.pages);
  return [...subs, ...packs];
}

/** The web catalog: pack sizes only — the page never renders its USD strings. */
export function getWebCatalog(): ProductOffering[] {
  return sortOfferings(WEB_OFFERINGS);
}

/**
 * The store's product offerings (native), sorted by sortOfferings.
 * When RevenueCat's offering has no packages or the call fails, the catalog comes from the store's products
 * (storeCatalog). null while RevenueCat is not configured or when the store has nothing either — the caller shows
 * an error with "Erneut versuchen", never the WEB_OFFERINGS placeholders (2026-10-02). The web uses getWebCatalog().
 */
/**
 * `Purchases.getOfferings()` returns the PurchasesOfferings object itself
 * (`{ all, current }`) — unlike `getCustomerInfo()` / `purchasePackage()`,
 * which wrap their result. Until 2026-09-05 this file destructured
 * `{ offerings }` from it and therefore saw NO offerings on either platform:
 * the purchase page showed the static catalog instead of store prices and
 * every purchase took the direct-product fallback (which is why purchases
 * still worked). Accept both shapes so a future plugin change can't
 * silently empty the paywall again.
 */
function unwrapOfferings(res: any): any {
  if (!res) return null;
  if (res.offerings && (res.offerings.all || res.offerings.current !== undefined)) return res.offerings;
  return res;
}
/** Exported for the Jest shape test only. */
export const unwrapOfferingsForTest = unwrapOfferings;

// Play Billing may return composite identifiers like "monthly_40:monthly-40".
// Strip the basePlanId suffix so the UI keeps using the clean product ID.
const cleanId = (id: string) => (id || '').split(':')[0];

const SUBSCRIPTION_IDS = ['monthly_40', 'annual_40'];
const PACK_IDS = ['credits_20', 'credits_60', 'credits_150'];

/**
 * The catalog straight from the store, for when RevenueCat's offering has no packages or the call fails (2026-10-02
 * review): the native page must never be left with nothing to buy. Play queries one product type per getProducts
 * call, so subscriptions and packs are two calls — the same lookup as purchaseProduct's direct-product path, which
 * then buys them. Reports the offerings failure (`reason`) with how many products the store gave instead; null when
 * the store has none either (→ the page's error state). Never throws.
 */
async function storeCatalog(reason: string): Promise<ProductOffering[] | null> {
  const lookup = async (ids: string[], type: 'SUBSCRIPTION' | 'NON_SUBSCRIPTION'): Promise<any[]> => {
    try {
      return (await Purchases.getProducts({ productIdentifiers: ids, type }))?.products || [];
    } catch (e) {
      reason += `; getProducts ${type}: ${describeError(e)}`;
      return [];
    }
  };
  const [subs, packs] = await Promise.all([lookup(SUBSCRIPTION_IDS, 'SUBSCRIPTION'), lookup(PACK_IDS, 'NON_SUBSCRIPTION')]);
  const items: ProductOffering[] = [];
  for (const [list, type] of [[subs, 'subscription'], [packs, 'pack']] as const) {
    for (const p of list) {
      const id = cleanId(p?.identifier);
      // One entry per product: Play lists a subscription once per base plan.
      if (!getPageCount(id) || items.some((o) => o.id === id)) continue;
      items.push({ id, title: p.title, description: p.description, priceString: p.priceString, pages: getPageCount(id), type });
    }
  }
  reportPurchaseError({ stage: 'offerings', error: reason, rc_initialized: rcInitialized, store_products: items.length });
  return items.length ? sortOfferings(items) : null;
}

export async function getOfferings(): Promise<ProductOffering[] | null> {
  if (!isNativeApp() || !rcInitialized || !Purchases) {
    return null;
  }

  try {
    const offerings = unwrapOfferings(await Purchases.getOfferings());
    // Fall back to any offering if none is marked current, so the purchase
    // page still renders even when the dashboard hasn't set a default.
    const allOfferings: any[] = [
      offerings?.current,
      ...(Object.values(offerings?.all || {}) as any[]),
    ].filter(Boolean);
    const chosen = allOfferings[0];
    if (!chosen?.availablePackages?.length) {
      return storeCatalog(`no packages; current=${offerings?.current?.identifier ?? 'none'}`);
    }

    return sortOfferings(chosen.availablePackages.map((pkg: any) => ({
      id: cleanId(pkg.product.identifier),
      title: pkg.product.title,
      description: pkg.product.description,
      priceString: pkg.product.priceString,
      pages: getPageCount(cleanId(pkg.product.identifier)),
      type: pkg.packageType === 'CUSTOM' ? 'pack' : 'subscription',
    })));
  } catch (e) {
    console.error('Error fetching offerings:', e);
    return storeCatalog(describeError(e));
  }
}

/**
 * Purchase a product by ID.
 * Only works on native. Returns receipt for server validation.
 *
 * RevenueCat Capacitor v12 removed `purchaseProduct` — we have to go via
 * `purchasePackage` with the Package object from the current Offering.
 */
export async function purchaseProduct(productId: string): Promise<PurchaseResult> {
  if (!isNativeApp() || !rcInitialized || !Purchases) {
    return {
      success: false, error: 'unavailable',
      detail: `RevenueCat not initialized (${lastInitError || 'no init attempted'})`,
    };
  }

  let offSummary = 'offerings: n/a';
  try {
    const offerings = unwrapOfferings(await Purchases.getOfferings());
    const allOfferings: any[] = [
      offerings?.current,
      ...(Object.values(offerings?.all || {}) as any[]),
    ].filter(Boolean);
    offSummary = `offerings current=${offerings?.current?.identifier ?? 'none'} all=[${Object.keys(offerings?.all || {}).join(',')}] packages=[${
      allOfferings.flatMap((o: any) => (o?.availablePackages || []).map((p: any) => p?.product?.identifier)).filter(Boolean).join(',')}]`;
    // Play Billing v6+ returns composite identifiers for subs:
    // "monthly_40:monthly-40" (productId:basePlanId). Match either form.
    const productMatches = (pid: string | undefined) =>
      !!pid && (pid === productId || pid.startsWith(productId + ':'));
    let pkg: any = null;
    for (const off of allOfferings) {
      pkg = off?.availablePackages?.find((p: any) => productMatches(p.product?.identifier));
      if (pkg) break;
    }

    // Only THIS purchase's store transaction id (2026-10-20): the backend dedupes validate against RevenueCat's
    // webhook on it, and both credit page packs. The old fallbacks — an earlier transaction of the same product,
    // or the RevenueCat user id — would credit a pack twice (or read a new pack as "already processed"). Without
    // an id the app skips validate and the webhook credits the purchase.
    const extractReceipt = (result: any): string => result?.transaction?.transactionIdentifier || '';

    if (pkg) {
      const result = await Purchases.purchasePackage({ aPackage: pkg });
      return { success: true, receiptId: extractReceipt(result), productId };
    }

    // Fallback when RC's offerings resolution drops a package: buy the
    // StoreProduct directly. Play Billing exposes SUBS and INAPP as two
    // distinct ProductTypes and RC's getProducts() queries one per call,
    // so we run both in parallel and merge.
    const [subs, inapp] = await Promise.all([
      Purchases.getProducts({ productIdentifiers: [productId], type: 'SUBSCRIPTION' }),
      Purchases.getProducts({ productIdentifiers: [productId], type: 'NON_SUBSCRIPTION' }),
    ]);
    const allProducts = [...(subs?.products || []), ...(inapp?.products || [])];
    const product = allProducts.find((p: any) => productMatches(p.identifier));
    if (product) {
      const result = await Purchases.purchaseStoreProduct({ product });
      return { success: true, receiptId: extractReceipt(result), productId };
    }

    console.warn('[RC] product not found:', productId);
    return {
      success: false, error: 'unavailable',
      detail: `product ${productId} not found; ${offSummary}; getProducts=[${allProducts.map((p: any) => p.identifier).join(',')}]`,
    };
  } catch (e: any) {
    if (isPurchaseCancelled(e)) {
      // Carry the store's own wording: StoreKit reports several sandbox
      // failures as "cancelled" (dismissed sheet, deferred/pending, sandbox
      // account prompts) and they are indistinguishable without it.
      return { success: false, error: 'cancelled', detail: describeError(e) };
    }
    console.error('[RC purchase error]', e);
    return { success: false, error: 'failed', detail: `${describeError(e)}; ${offSummary}` };
  }
}

export interface SubscriptionStatus {
  active: boolean;
  willRenew: boolean;
  expirationDate: string | null;
  store: 'PLAY_STORE' | 'APP_STORE' | 'MAC_APP_STORE' | 'STRIPE' | 'PROMOTIONAL' | 'AMAZON' | 'UNKNOWN_STORE' | null;
}

/**
 * Returns per-product subscription status keyed by clean productId
 * (strips Play's "productId:basePlanId" suffix). Uses RC's customerInfo
 * as the source of truth — it merges Play + App Store state.
 *
 * Empty map on web / uninitialized SDK.
 */
/**
 * Drop RevenueCat's cached customer info (it caches for ~5 minutes) so the
 * next getCustomerInfo() reflects a purchase or plan change that just
 * happened — otherwise the purchase page kept showing the old plan as
 * "current" right after a monthly → annual upgrade (iOS sandbox, 2026-09-05).
 * Best-effort: failures are ignored, the page reload re-fetches anyway.
 */
export async function refreshCustomerInfo(): Promise<void> {
  if (!isNativeApp() || !rcInitialized || !Purchases) return;
  try {
    await Purchases.invalidateCustomerInfoCache();
    await Purchases.getCustomerInfo();
  } catch {
    /* ignore */
  }
}

export async function getSubscriptionStatuses(): Promise<Record<string, SubscriptionStatus>> {
  if (!isNativeApp() || !rcInitialized || !Purchases) return {};

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const out: Record<string, SubscriptionStatus> = {};
    const active = customerInfo?.entitlements?.active || {};
    for (const entId of Object.keys(active)) {
      const e = active[entId];
      const rawId: string = e?.productIdentifier || '';
      const cleanId = rawId.split(':')[0];
      if (!cleanId) continue;
      out[cleanId] = {
        active: true,
        willRenew: !!e?.willRenew,
        expirationDate: e?.expirationDate || null,
        store: e?.store || null,
      };
    }
    return out;
  } catch (e) {
    console.error('getSubscriptionStatuses failed:', e);
    return {};
  }
}

/**
 * Opens the store's manage-subscription UI.
 *
 * - Play: deep-links to the specific subscription (sku + package).
 * - App Store: iOS only supports a generic subscriptions list, no
 *   per-product deeplink — productId ignored.
 *
 * No-op on web.
 */
export function openManageSubscription(productId: string, packageName: string): void {
  if (!isNativeApp()) return;
  const platform = getPlatform();
  const url = platform === 'ios'
    ? 'itms-apps://apps.apple.com/account/subscriptions'
    : `https://play.google.com/store/account/subscriptions?sku=${encodeURIComponent(productId)}&package=${encodeURIComponent(packageName)}`;
  // Capacitor routes `_system` targets to the external handler (Play
  // Store / App Store app), matching the platform scheme.
  window.open(url, '_system');
}

/**
 * Restore previous purchases (e.g., after reinstall).
 */
export async function restorePurchases(): Promise<boolean> {
  if (!isNativeApp() || !rcInitialized || !Purchases) return false;

  try {
    await Purchases.restorePurchases();
    return true;
  } catch (e) {
    console.error('Restore failed:', e);
    return false;
  }
}

function getPageCount(productId: string): number {
  const map: Record<string, number> = {
    credits_20: 20,
    credits_60: 60,
    credits_150: 150,
    monthly_40: 40,
    annual_40: 40,
  };
  return map[productId] || 0;
}
